import { getSupabaseClient } from '@/integrations/supabase/client';
import type { ActiveOperator } from '@/stores/auth/auth.operator';
import type { Tab } from '@/types';

export type SaleSellerRef =
  | { type: 'member'; userId: string }
  | { type: 'simple_user'; simpleUserId: string };

function normalizeSellerLabel(label: string): string {
  return label.trim().toLowerCase();
}

function profileMatchesLabel(
  profile: { full_name?: string | null; username?: string | null },
  normalizedLabel: string,
): boolean {
  const fullName = normalizeSellerLabel(String(profile.full_name ?? ''));
  const username = normalizeSellerLabel(String(profile.username ?? ''));
  return fullName === normalizedLabel || username === normalizedLabel;
}

export async function resolveSaleSellerFromServerName(
  organizationId: string,
  serverName: string,
): Promise<SaleSellerRef | null> {
  const label = serverName.trim();
  if (!label) return null;

  const normalizedLabel = normalizeSellerLabel(label);
  const supabase = getSupabaseClient();

  // 1) Members first (same order as TabConfig). Include inactive members so
  // legacy tabs with stale server_name still resolve after someone leaves.
  const { data: memberships, error: membershipsError } = await supabase
    .from('organization_memberships')
    .select('user_id')
    .eq('organization_id', organizationId);

  if (membershipsError) throw membershipsError;

  const userIds = [...new Set((memberships ?? []).map((m) => m.user_id as string))];
  if (userIds.length > 0) {
    const { data: profiles, error: profilesError } = await supabase
      .from('profiles')
      .select('id, username, full_name')
      .in('id', userIds);

    if (profilesError) throw profilesError;

    const member = (profiles ?? []).find((p) => profileMatchesLabel(p, normalizedLabel));

    if (member?.id) {
      return { type: 'member', userId: member.id };
    }
  }

  // 2) Simple users (active first, then inactive for legacy tabs)
  for (const isActive of [true, false]) {
    const { data: simpleUser, error: simpleUserError } = await supabase
      .from('simple_users')
      .select('id, username')
      .eq('organization_id', organizationId)
      .eq('is_active', isActive);

    if (simpleUserError) throw simpleUserError;

    const match = (simpleUser ?? []).find(
      (user) => normalizeSellerLabel(String(user.username ?? '')) === normalizedLabel,
    );

    if (match?.id) {
      return { type: 'simple_user', simpleUserId: match.id };
    }
  }

  return null;
}

/** Fallback when tab.server_name cannot be matched (stale label on legacy tabs). */
export function resolveSaleSellerFromActiveOperator(auth: {
  user: { id: string } | null;
  activeOperator: ActiveOperator | null;
}): SaleSellerRef | null {
  if (!auth.user?.id) return null;

  if (auth.activeOperator?.type === 'simple_user') {
    return { type: 'simple_user', simpleUserId: auth.activeOperator.id };
  }

  if (auth.activeOperator?.type === 'member') {
    return { type: 'member', userId: auth.user.id };
  }

  return null;
}

export function getTabServerLabel(
    tab: Tab,
    auth: {
      profile: { full_name?: string | null; username?: string } | null;
      activeOperator: import('@/stores/auth/auth.operator').ActiveOperator | null;
    },
  ): string | undefined {
    if (tab.serverName?.trim()) return tab.serverName.trim();
  
    if (auth.activeOperator?.type === 'simple_user') {
      return auth.activeOperator.username;
    }
  
    if (auth.activeOperator?.type === 'member') {
      return auth.profile?.full_name?.trim() || auth.profile?.username?.trim();
    }
  
    return undefined;
  }